import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../../axiosConfig';

// Tab options
const TABS = [
  { id: 'membership', label: 'Membership Info' },
  { id: 'team', label: 'Team' },
  { id: 'billing', label: 'Billing' },
];

// Month options for season selects
const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

// Team Section Component
function TeamSection({ clubData }) {
  const [admins, setAdmins] = useState([]);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canInvite, setCanInvite] = useState(false);
  const [maxAdmins, setMaxAdmins] = useState(3);
  const [currentCount, setCurrentCount] = useState(0);

  // Invite modal state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState('');

  // Fetch admins
  const fetchAdmins = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('myclub/admins/');
      setAdmins(response.data.admins || []);
      setPendingInvitations(response.data.pending_invitations || []);
      setCanInvite(response.data.can_invite || false);
      setMaxAdmins(response.data.max_admins || 3);
      setCurrentCount(response.data.current_count || 0);
    } catch (error) {
      console.error('Failed to fetch admins:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviteError('');
    setInviteSuccess('');

    if (!inviteEmail.trim()) {
      setInviteError('Email is required');
      return;
    }

    setInviting(true);
    try {
      const response = await axiosInstance.post('myclub/admins/invite/', {
        email: inviteEmail.trim().toLowerCase()
      });
      setInviteSuccess(response.data.message || 'Invitation sent successfully!');
      setInviteEmail('');
      fetchAdmins(); // Refresh the list
      setTimeout(() => {
        setShowInviteModal(false);
        setInviteSuccess('');
      }, 2000);
    } catch (error) {
      setInviteError(error.response?.data?.error || 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  const handleRevokeInvitation = async (invitationId) => {
    if (!window.confirm('Are you sure you want to revoke this invitation?')) return;

    try {
      await axiosInstance.delete(`myclub/admins/invitation/${invitationId}/revoke/`);
      fetchAdmins();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to revoke invitation');
    }
  };

  const handleRemoveAdmin = async (adminId, adminName) => {
    if (!window.confirm(`Are you sure you want to remove ${adminName} as an admin?`)) return;

    try {
      await axiosInstance.delete(`myclub/admins/${adminId}/remove/`);
      fetchAdmins();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to remove admin');
    }
  };

  const formatDate = (dateString) => {
    const options = { month: 'short', day: 'numeric', year: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  if (loading) {
    return (
      <div className="team-section">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading team members...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="team-section">
      <div className="team-header">
        <div>
          <h3>Team Members</h3>
          <p className="team-subtitle">
            {currentCount} of {maxAdmins} admin slots used
          </p>
        </div>
        {canInvite && (
          <button
            className="invite-btn"
            onClick={() => setShowInviteModal(true)}
          >
            + Invite Admin
          </button>
        )}
      </div>

      {/* Current Admins */}
      <div className="team-list">
        {admins.length === 0 ? (
          <div className="team-empty">
            <p>No team members yet. Invite your first admin to get started.</p>
          </div>
        ) : (
          admins.map((admin) => (
            <div key={admin.id} className="team-member">
              <div className="member-info">
                <div className="member-name">
                  {admin.first_name && admin.last_name
                    ? `${admin.first_name} ${admin.last_name}`
                    : admin.email}
                  {admin.role === 'owner' && (
                    <span className="role-badge owner">Owner</span>
                  )}
                  {admin.role === 'admin' && (
                    <span className="role-badge admin">Admin</span>
                  )}
                </div>
                <div className="member-email">{admin.email}</div>
              </div>
              {admin.role !== 'owner' && (
                <button
                  className="remove-btn"
                  onClick={() => handleRemoveAdmin(admin.id, admin.email)}
                  title="Remove admin"
                >
                  Remove
                </button>
              )}
            </div>
          ))
        )}
      </div>

      {/* Pending Invitations */}
      {pendingInvitations.length > 0 && (
        <div className="pending-invitations">
          <h4>Pending Invitations</h4>
          <div className="invitation-list">
            {pendingInvitations.map((inv) => (
              <div key={inv.id} className="invitation-item">
                <div className="invitation-info">
                  <div className="invitation-email">{inv.email}</div>
                  <div className="invitation-meta">
                    Expires {formatDate(inv.expires_at)}
                  </div>
                </div>
                <button
                  className="revoke-btn"
                  onClick={() => handleRevokeInvitation(inv.id)}
                  title="Revoke invitation"
                >
                  Revoke
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="modal-overlay" onClick={() => setShowInviteModal(false)}>
          <div className="invite-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Invite Team Member</h3>
              <button
                className="modal-close"
                onClick={() => setShowInviteModal(false)}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <form onSubmit={handleInvite}>
              <div className="modal-body">
                <p className="modal-description">
                  Invite a team member to help manage your club's dashboard.
                  They'll receive an email with instructions to create an account or log in.
                </p>

                {inviteError && (
                  <div className="form-message error">{inviteError}</div>
                )}
                {inviteSuccess && (
                  <div className="form-message success">{inviteSuccess}</div>
                )}

                <div className="form-field">
                  <label>Email Address</label>
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="colleague@club.com"
                    disabled={inviting}
                    autoFocus
                  />
                </div>

                <p className="modal-hint">
                  {maxAdmins - currentCount} invitation slot{maxAdmins - currentCount !== 1 ? 's' : ''} remaining
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowInviteModal(false)}
                  disabled={inviting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={inviting || !inviteEmail.trim()}
                >
                  {inviting ? 'Sending...' : 'Send Invitation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// Billing Section Component
function BillingSection({ clubData }) {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchSubscription = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('myclub/subscription/');
      setSubscription(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const handleUpgrade = async () => {
    setActionLoading(true);
    try {
      const response = await axiosInstance.post('myclub/subscription/checkout/', {
        test_mode: true // TODO: Remove for production
      });
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to start checkout. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleManageBilling = async () => {
    setActionLoading(true);
    try {
      const response = await axiosInstance.post('myclub/subscription/portal/', {
        test_mode: true // TODO: Remove for production
      });
      if (response.data.portal_url) {
        window.location.href = response.data.portal_url;
      }
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to open billing portal. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const options = { month: 'long', day: 'numeric', year: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  if (loading) {
    return (
      <div className="billing-section">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading billing information...</p>
        </div>
      </div>
    );
  }

  const isSubscribed = subscription?.is_subscribed;
  const cancelAtPeriodEnd = subscription?.cancel_at_period_end;

  return (
    <div className="billing-section">
      {isSubscribed ? (
        <>
          <div className="billing-status">
            <div className={`billing-status-badge ${cancelAtPeriodEnd ? 'canceling' : 'active'}`}>
              {cancelAtPeriodEnd ? 'Canceling' : 'Pro Plan'}
            </div>
            {cancelAtPeriodEnd ? (
              <p className="billing-description billing-cancel-notice">
                Your subscription will end on {formatDate(subscription.current_period_end)}.
                You can reactivate anytime before then.
              </p>
            ) : (
              <p className="billing-description">
                You have unlimited access to discover and connect with prospects.
              </p>
            )}
          </div>

          <div className="billing-details">
            <div className="billing-row">
              <span className="billing-label">Status</span>
              <span className={`billing-value ${cancelAtPeriodEnd ? 'billing-status-canceling' : ''}`}>
                {cancelAtPeriodEnd ? 'Cancels at period end' : 'Active'}
              </span>
            </div>
            <div className="billing-row">
              <span className="billing-label">Amount</span>
              <span className="billing-value">${subscription.billing_amount}/month</span>
            </div>
            {subscription.current_period_end && (
              <div className="billing-row">
                <span className="billing-label">
                  {cancelAtPeriodEnd ? 'Access until' : 'Next billing date'}
                </span>
                <span className="billing-value">{formatDate(subscription.current_period_end)}</span>
              </div>
            )}
          </div>

          <button
            className="billing-manage-btn"
            onClick={handleManageBilling}
            disabled={actionLoading}
          >
            {actionLoading ? 'Loading...' : cancelAtPeriodEnd ? 'Reactivate Subscription' : 'Manage Billing'}
          </button>
        </>
      ) : (
        <>
          <div className="billing-status">
            <div className="billing-status-badge free">Free Plan</div>
            <p className="billing-description">
              You're on the free plan with {subscription?.requests_remaining || 0} of {subscription?.free_request_limit || 5} discovery requests remaining.
            </p>
          </div>

          <div className="billing-upgrade-card">
            <h4>Upgrade to Pro</h4>
            <p className="billing-upgrade-price">$250<span>/month</span></p>
            <ul className="billing-upgrade-features">
              <li>Unlimited discovery requests</li>
              <li>Connect with prospects in your area</li>
              <li>Priority support</li>
            </ul>
            <button
              className="billing-upgrade-btn"
              onClick={handleUpgrade}
              disabled={actionLoading}
            >
              {actionLoading ? 'Loading...' : 'Upgrade to Pro'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function MyClubSection({ clubData }) {
  const [activeTab, setActiveTab] = useState('membership');
  const [formData, setFormData] = useState({
    // Contact Info
    contact_name: '',
    contact_email: '',
    contact_position: '',

    // Club Info
    invite_only: null,

    // Ownership
    member_owned: null,
    parent_company: '',
    true_equity_club: null,

    // Initiation Refund
    initiation_refundable: null,
    initiation_refund_percent: '',

    // Golf Season
    golf_season_start: '',
    golf_season_end: '',

    // Waitlist - Full Golf
    waitlist_full_golf: null,
    waitlist_full_golf_months: '',

    // Waitlist - Young Executive
    waitlist_young_exec: null,
    waitlist_young_exec_months: '',

    // Waitlist - Social
    waitlist_social: null,
    waitlist_social_months: '',

    // Financial - Full Golf
    full_golf_initiation: '',
    full_golf_annual_dues: '',

    // Financial - Young Executive
    young_exec_initiation: '',
    young_exec_annual_dues: '',

    // Financial - Social
    social_initiation: '',
    social_annual_dues: '',

    // Financial - National
    offers_national_membership: null,
    national_initiation: '',
    national_annual_dues: '',

    // Notes
    club_notes: '',
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [hasExistingData, setHasExistingData] = useState(false);

  // Membership packet state
  const [membershipPacketUrl, setMembershipPacketUrl] = useState(null);
  const [uploadingPacket, setUploadingPacket] = useState(false);
  const [packetMessage, setPacketMessage] = useState({ type: '', text: '' });
  const [showPdfModal, setShowPdfModal] = useState(false);

  // Fetch existing membership info
  const fetchMembershipInfo = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('myclub/membership-info/');
      if (response.data && response.data.membership_info) {
        const info = response.data.membership_info;
        setFormData(prev => ({
          ...prev,
          ...info,
          // Ensure null values are handled properly for selects
          golf_season_start: info.golf_season_start || '',
          golf_season_end: info.golf_season_end || '',
          initiation_refund_percent: info.initiation_refund_percent || '',
          waitlist_full_golf_months: info.waitlist_full_golf_months || '',
          waitlist_young_exec_months: info.waitlist_young_exec_months || '',
          waitlist_social_months: info.waitlist_social_months || '',
          full_golf_initiation: info.full_golf_initiation || '',
          full_golf_annual_dues: info.full_golf_annual_dues || '',
          young_exec_initiation: info.young_exec_initiation || '',
          young_exec_annual_dues: info.young_exec_annual_dues || '',
          social_initiation: info.social_initiation || '',
          social_annual_dues: info.social_annual_dues || '',
          national_initiation: info.national_initiation || '',
          national_annual_dues: info.national_annual_dues || '',
        }));
        setMembershipPacketUrl(info.membership_packet_url || null);
        setHasExistingData(true);
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Failed to fetch membership info:', error);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMembershipInfo();
  }, [fetchMembershipInfo]);

  const handleChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };

      // Decision tree logic - clear dependent fields when parent changes
      if (field === 'member_owned') {
        if (value === true) {
          updated.parent_company = '';
        } else if (value === false) {
          updated.true_equity_club = null;
        }
      }

      if (field === 'initiation_refundable' && value === false) {
        updated.initiation_refund_percent = '';
      }

      if (field === 'waitlist_full_golf' && value === false) {
        updated.waitlist_full_golf_months = '';
      }

      if (field === 'waitlist_young_exec' && value === false) {
        updated.waitlist_young_exec_months = '';
      }

      if (field === 'waitlist_social' && value === false) {
        updated.waitlist_social_months = '';
      }

      if (field === 'offers_national_membership' && value === false) {
        updated.national_initiation = '';
        updated.national_annual_dues = '';
      }

      return updated;
    });

    // Clear any existing message when user makes changes
    if (message.text) {
      setMessage({ type: '', text: '' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      // Prepare data - convert empty strings to null for numeric fields
      const dataToSend = {
        ...formData,
        golf_season_start: formData.golf_season_start || null,
        golf_season_end: formData.golf_season_end || null,
        initiation_refund_percent: formData.initiation_refund_percent || null,
        waitlist_full_golf_months: formData.waitlist_full_golf_months || null,
        waitlist_young_exec_months: formData.waitlist_young_exec_months || null,
        waitlist_social_months: formData.waitlist_social_months || null,
        full_golf_initiation: formData.full_golf_initiation || null,
        full_golf_annual_dues: formData.full_golf_annual_dues || null,
        young_exec_initiation: formData.young_exec_initiation || null,
        young_exec_annual_dues: formData.young_exec_annual_dues || null,
        social_initiation: formData.social_initiation || null,
        social_annual_dues: formData.social_annual_dues || null,
        national_initiation: formData.national_initiation || null,
        national_annual_dues: formData.national_annual_dues || null,
      };

      await axiosInstance.post('myclub/membership-info/', dataToSend);
      setMessage({ type: 'success', text: 'Club information saved successfully!' });
      setHasExistingData(true);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to save. Please try again.'
      });
    } finally {
      setSaving(false);
    }
  };

  // Handle membership packet upload
  const handlePacketUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setPacketMessage({ type: 'error', text: 'Only PDF files are allowed.' });
      return;
    }

    // Validate file size (50MB max)
    if (file.size > 50 * 1024 * 1024) {
      setPacketMessage({ type: 'error', text: 'File size exceeds 50MB limit.' });
      return;
    }

    setUploadingPacket(true);
    setPacketMessage({ type: '', text: '' });

    try {
      const formData = new FormData();
      formData.append('membership_packet', file);

      const response = await axiosInstance.post(
        'myclub/membership-info/upload-packet/',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      setMembershipPacketUrl(response.data.membership_packet_url);
      setPacketMessage({ type: 'success', text: 'Membership packet uploaded successfully!' });
    } catch (error) {
      setPacketMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to upload. Please try again.'
      });
    } finally {
      setUploadingPacket(false);
      // Reset file input
      e.target.value = '';
    }
  };

  // Handle membership packet removal
  const handlePacketRemove = async () => {
    if (!window.confirm('Are you sure you want to remove the membership packet?')) return;

    setUploadingPacket(true);
    setPacketMessage({ type: '', text: '' });

    try {
      await axiosInstance.delete('myclub/membership-info/upload-packet/');
      setMembershipPacketUrl(null);
      setPacketMessage({ type: 'success', text: 'Membership packet removed.' });
    } catch (error) {
      setPacketMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to remove. Please try again.'
      });
    } finally {
      setUploadingPacket(false);
    }
  };

  // Boolean select component
  const BooleanSelect = ({ label, value, onChange, helpText }) => (
    <div className="form-field">
      <label>{label}</label>
      <div className="boolean-select">
        <button
          type="button"
          className={`bool-btn ${value === true ? 'active' : ''}`}
          onClick={() => onChange(true)}
        >
          Yes
        </button>
        <button
          type="button"
          className={`bool-btn ${value === false ? 'active' : ''}`}
          onClick={() => onChange(false)}
        >
          No
        </button>
      </div>
      {helpText && <span className="help-text">{helpText}</span>}
    </div>
  );

  if (loading) {
    return (
      <div className="content-section">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading club information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="content-section myclub-section">
      <div className="section-header">
        <h1>My Club</h1>
      </div>

      {/* Tab Navigation */}
      <div className="myclub-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            className={`myclub-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Membership Info Tab */}
      {activeTab === 'membership' && (
        <>
          {message.text && (
            <div className={`form-message ${message.type}`}>
              {message.text}
            </div>
          )}

          <form onSubmit={handleSubmit} className="myclub-form">
        {/* Contact Information */}
        <div className="form-section">
          <h3>Contact Information</h3>
          <div className="form-grid">
            <div className="form-field">
              <label>Contact Name</label>
              <input
                type="text"
                value={formData.contact_name}
                onChange={(e) => handleChange('contact_name', e.target.value)}
                placeholder="John Smith"
              />
            </div>
            <div className="form-field">
              <label>Contact Email</label>
              <input
                type="email"
                value={formData.contact_email}
                onChange={(e) => handleChange('contact_email', e.target.value)}
                placeholder="john@club.com"
              />
            </div>
            <div className="form-field">
              <label>Position / Title</label>
              <input
                type="text"
                value={formData.contact_position}
                onChange={(e) => handleChange('contact_position', e.target.value)}
                placeholder="Membership Director"
              />
            </div>
          </div>
        </div>

        {/* Club Information */}
        <div className="form-section">
          <h3>Club Information</h3>
          <BooleanSelect
            label="Is your club invite-only?"
            value={formData.invite_only}
            onChange={(val) => handleChange('invite_only', val)}
            helpText="Do prospective members need a sponsor or invitation to apply?"
          />

          {/* Membership Packet Upload */}
          <div className="form-field membership-packet-field">
            <label>Membership Packet</label>
            <p className="field-description">
              Upload your club's membership packet PDF to share with prospective members.
            </p>

            {packetMessage.text && (
              <div className={`packet-message ${packetMessage.type}`}>
                {packetMessage.text}
              </div>
            )}

            {membershipPacketUrl ? (
              <div className="packet-current">
                <div className="packet-info">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <line x1="16" y1="13" x2="8" y2="13"/>
                    <line x1="16" y1="17" x2="8" y2="17"/>
                    <polyline points="10 9 9 9 8 9"/>
                  </svg>
                  <span>Membership Packet (PDF)</span>
                </div>
                <div className="packet-actions">
                  <button
                    type="button"
                    className="packet-btn view"
                    onClick={() => setShowPdfModal(true)}
                  >
                    View
                  </button>
                  <button
                    type="button"
                    className="packet-btn remove"
                    onClick={handlePacketRemove}
                    disabled={uploadingPacket}
                  >
                    {uploadingPacket ? 'Removing...' : 'Remove'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="packet-upload">
                <label className={`upload-area ${uploadingPacket ? 'uploading' : ''}`}>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handlePacketUpload}
                    disabled={uploadingPacket}
                  />
                  {uploadingPacket ? (
                    <>
                      <div className="upload-spinner"></div>
                      <span>Uploading...</span>
                    </>
                  ) : (
                    <>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="17 8 12 3 7 8"/>
                        <line x1="12" y1="3" x2="12" y2="15"/>
                      </svg>
                      <span>Click to upload PDF</span>
                      <span className="upload-hint">Max file size: 50MB</span>
                    </>
                  )}
                </label>
              </div>
            )}
          </div>
        </div>

        {/* Ownership Structure */}
        <div className="form-section">
          <h3>Ownership Structure</h3>
          <BooleanSelect
            label="Is your club member-owned?"
            value={formData.member_owned}
            onChange={(val) => handleChange('member_owned', val)}
          />

          {formData.member_owned === false && (
            <div className="form-field conditional-field">
              <label>Parent Company</label>
              <input
                type="text"
                value={formData.parent_company}
                onChange={(e) => handleChange('parent_company', e.target.value)}
                placeholder="e.g., Invited, Troon, Concert Golf Partners"
              />
            </div>
          )}

          {formData.member_owned === true && (
            <div className="conditional-field">
              <BooleanSelect
                label="Is this a true equity club?"
                value={formData.true_equity_club}
                onChange={(val) => handleChange('true_equity_club', val)}
                helpText="Would members receive proceeds proportional to ownership if the club were sold?"
              />
            </div>
          )}
        </div>

        {/* Initiation Refund */}
        <div className="form-section">
          <h3>Initiation Fee Refund Policy</h3>
          <BooleanSelect
            label="Is the initiation fee refundable?"
            value={formData.initiation_refundable}
            onChange={(val) => handleChange('initiation_refundable', val)}
          />

          {formData.initiation_refundable === true && (
            <div className="form-field conditional-field">
              <label>Refund Percentage</label>
              <div className="input-with-suffix">
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={formData.initiation_refund_percent}
                  onChange={(e) => handleChange('initiation_refund_percent', e.target.value)}
                  placeholder="e.g., 80"
                />
                <span className="suffix">%</span>
              </div>
              <span className="help-text">Percentage returned upon membership replacement</span>
            </div>
          )}
        </div>

        {/* Golf Season */}
        <div className="form-section">
          <h3>Golf Season</h3>
          <div className="form-grid two-col">
            <div className="form-field">
              <label>Season Start</label>
              <select
                value={formData.golf_season_start}
                onChange={(e) => handleChange('golf_season_start', e.target.value ? parseInt(e.target.value) : '')}
              >
                <option value="">Select month</option>
                {MONTHS.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
            <div className="form-field">
              <label>Season End</label>
              <select
                value={formData.golf_season_end}
                onChange={(e) => handleChange('golf_season_end', e.target.value ? parseInt(e.target.value) : '')}
              >
                <option value="">Select month</option>
                {MONTHS.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
          </div>
          <span className="help-text section-help">Leave blank if your club operates year-round</span>
        </div>

        {/* Waitlist Information */}
        <div className="form-section">
          <h3>Waitlist Information</h3>

          {/* Full Golf Membership */}
          <div className="waitlist-group">
            <BooleanSelect
              label="Is there a waitlist for Full Golf membership?"
              value={formData.waitlist_full_golf}
              onChange={(val) => handleChange('waitlist_full_golf', val)}
            />
            {formData.waitlist_full_golf === true && (
              <div className="form-field conditional-field inline-field">
                <label>Estimated wait time</label>
                <div className="input-with-suffix">
                  <input
                    type="number"
                    min="0"
                    value={formData.waitlist_full_golf_months}
                    onChange={(e) => handleChange('waitlist_full_golf_months', e.target.value)}
                    placeholder="0"
                  />
                  <span className="suffix">months</span>
                </div>
              </div>
            )}
          </div>

          {/* Young Executive Membership */}
          <div className="waitlist-group">
            <BooleanSelect
              label="Is there a waitlist for Young Executive membership?"
              value={formData.waitlist_young_exec}
              onChange={(val) => handleChange('waitlist_young_exec', val)}
            />
            {formData.waitlist_young_exec === true && (
              <div className="form-field conditional-field inline-field">
                <label>Estimated wait time</label>
                <div className="input-with-suffix">
                  <input
                    type="number"
                    min="0"
                    value={formData.waitlist_young_exec_months}
                    onChange={(e) => handleChange('waitlist_young_exec_months', e.target.value)}
                    placeholder="0"
                  />
                  <span className="suffix">months</span>
                </div>
              </div>
            )}
          </div>

          {/* Social Membership */}
          <div className="waitlist-group">
            <BooleanSelect
              label="Is there a waitlist for Social membership?"
              value={formData.waitlist_social}
              onChange={(val) => handleChange('waitlist_social', val)}
            />
            {formData.waitlist_social === true && (
              <div className="form-field conditional-field inline-field">
                <label>Estimated wait time</label>
                <div className="input-with-suffix">
                  <input
                    type="number"
                    min="0"
                    value={formData.waitlist_social_months}
                    onChange={(e) => handleChange('waitlist_social_months', e.target.value)}
                    placeholder="0"
                  />
                  <span className="suffix">months</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Membership Pricing */}
        <div className="form-section">
          <h3>Membership Pricing</h3>
          <p className="section-description">
            Providing pricing helps prospective members understand if your club is within their budget.
            All fields are optional.
          </p>

          {/* Full Golf */}
          <div className="pricing-group">
            <h4>Full Golf Membership</h4>
            <div className="form-grid two-col">
              <div className="form-field">
                <label>Initiation Fee</label>
                <div className="input-with-prefix">
                  <span className="prefix">$</span>
                  <input
                    type="number"
                    min="0"
                    value={formData.full_golf_initiation}
                    onChange={(e) => handleChange('full_golf_initiation', e.target.value)}
                    placeholder="0"
                  />
                </div>
              </div>
              <div className="form-field">
                <label>Annual Dues</label>
                <div className="input-with-prefix">
                  <span className="prefix">$</span>
                  <input
                    type="number"
                    min="0"
                    value={formData.full_golf_annual_dues}
                    onChange={(e) => handleChange('full_golf_annual_dues', e.target.value)}
                    placeholder="0"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Young Executive */}
          <div className="pricing-group">
            <h4>Young Executive Membership</h4>
            <div className="form-grid two-col">
              <div className="form-field">
                <label>Initiation Fee</label>
                <div className="input-with-prefix">
                  <span className="prefix">$</span>
                  <input
                    type="number"
                    min="0"
                    value={formData.young_exec_initiation}
                    onChange={(e) => handleChange('young_exec_initiation', e.target.value)}
                    placeholder="0"
                  />
                </div>
              </div>
              <div className="form-field">
                <label>Annual Dues</label>
                <div className="input-with-prefix">
                  <span className="prefix">$</span>
                  <input
                    type="number"
                    min="0"
                    value={formData.young_exec_annual_dues}
                    onChange={(e) => handleChange('young_exec_annual_dues', e.target.value)}
                    placeholder="0"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Social */}
          <div className="pricing-group">
            <h4>Social Membership</h4>
            <div className="form-grid two-col">
              <div className="form-field">
                <label>Initiation Fee</label>
                <div className="input-with-prefix">
                  <span className="prefix">$</span>
                  <input
                    type="number"
                    min="0"
                    value={formData.social_initiation}
                    onChange={(e) => handleChange('social_initiation', e.target.value)}
                    placeholder="0"
                  />
                </div>
              </div>
              <div className="form-field">
                <label>Annual Dues</label>
                <div className="input-with-prefix">
                  <span className="prefix">$</span>
                  <input
                    type="number"
                    min="0"
                    value={formData.social_annual_dues}
                    onChange={(e) => handleChange('social_annual_dues', e.target.value)}
                    placeholder="0"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* National Membership */}
          <div className="pricing-group">
            <h4>National Membership</h4>
            <BooleanSelect
              label="Do you offer a National Membership?"
              value={formData.offers_national_membership}
              onChange={(val) => handleChange('offers_national_membership', val)}
            />
            {formData.offers_national_membership === true && (
              <div className="form-grid two-col conditional-field">
                <div className="form-field">
                  <label>Initiation Fee</label>
                  <div className="input-with-prefix">
                    <span className="prefix">$</span>
                    <input
                      type="number"
                      min="0"
                      value={formData.national_initiation}
                      onChange={(e) => handleChange('national_initiation', e.target.value)}
                      placeholder="0"
                    />
                  </div>
                </div>
                <div className="form-field">
                  <label>Annual Dues</label>
                  <div className="input-with-prefix">
                    <span className="prefix">$</span>
                    <input
                      type="number"
                      min="0"
                      value={formData.national_annual_dues}
                      onChange={(e) => handleChange('national_annual_dues', e.target.value)}
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Additional Notes */}
        <div className="form-section">
          <h3>Additional Notes</h3>
          <div className="form-field">
            <label>Notes for Prospective Members</label>
            <textarea
              value={formData.club_notes}
              onChange={(e) => handleChange('club_notes', e.target.value)}
              placeholder="Any additional information you'd like to share with prospective members..."
              rows={4}
            />
          </div>
        </div>

        {/* Submit */}
        <div className="form-actions">
          <button type="submit" className="submit-btn" disabled={saving}>
            {saving ? 'Saving...' : hasExistingData ? 'Update Information' : 'Save Information'}
          </button>
        </div>
      </form>

          {/* PDF Viewer Modal */}
          {showPdfModal && membershipPacketUrl && (
            <div className="pdf-modal-overlay" onClick={() => setShowPdfModal(false)}>
              <div className="pdf-modal" onClick={(e) => e.stopPropagation()}>
                <div className="pdf-modal-header">
                  <h3>Membership Packet</h3>
                  <button
                    type="button"
                    className="pdf-modal-close"
                    onClick={() => setShowPdfModal(false)}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18"/>
                      <line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                </div>
                <div className="pdf-modal-body">
                  <iframe
                    src={membershipPacketUrl}
                    title="Membership Packet"
                    width="100%"
                    height="100%"
                  />
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Team Tab */}
      {activeTab === 'team' && (
        <TeamSection clubData={clubData} />
      )}

      {/* Billing Tab */}
      {activeTab === 'billing' && (
        <BillingSection clubData={clubData} />
      )}
    </div>
  );
}

export default MyClubSection;
